import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, handleApiError } from '../lib/api-client';
import { getCurrentUser } from '../hooks/useAuth';
import { Input } from '../components/ui/Input';
import logoRw from '../logo-rw.png';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(6, 'New password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine(d => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export default function ChangePassword() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = getCurrentUser();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    mutationFn: (data: FormData) => authApi.changePassword(data.currentPassword, data.newPassword),
    onSuccess: () => {
      // Update stored user — clear the mustChangePassword flag
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed?.user) {
            parsed.user.mustChangePassword = false;
          } else if (parsed?.mustChangePassword !== undefined) {
            parsed.mustChangePassword = false;
          }
          localStorage.setItem('user', JSON.stringify(parsed));
        } catch {}
      }
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      toast.success('Password changed successfully. Please log in again.');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      navigate('/login');
    },
    onError: handleApiError,
  });

  const onSubmit = (data: FormData) => mutation.mutate(data);

  return (
    <div className='min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4'>
      <div className='max-w-sm w-full'>
        <div className='bg-white rounded-lg p-8'>
          <div className='text-center mb-8'>
            <div className='flex justify-center mb-4'>
              <img src={logoRw} alt='Republic of Rwanda Logo' className='h-16 w-16' />
            </div>
            <h1 className='text-2xl font-bold text-gray-900 mb-1'>Change Password</h1>
            <p className='text-sm text-gray-500'>
              {user?.name ? `Hello ${user.name}, you` : 'You'} must set a new password before continuing.
            </p>
          </div>

          <div className='mb-5 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800'>
            Your account was created with a temporary password. Please choose a new password to proceed.
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Current (temporary) password</label>
              <Input type='password' error={errors.currentPassword?.message} {...register('currentPassword')} />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>New password</label>
              <Input type='password' error={errors.newPassword?.message} {...register('newPassword')} />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Confirm new password</label>
              <Input type='password' error={errors.confirmPassword?.message} {...register('confirmPassword')} />
            </div>

            <button
              type='submit'
              disabled={mutation.isPending}
              className='w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin inline mr-2' />
                  Changing password…
                </>
              ) : (
                'Set new password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
